import React, { useState, useEffect, useRef, useCallback } from "react";
import { http, API } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { Radio, Upload, Video, X, Loader2, Waves, Settings2, Play } from "lucide-react";
import { toast } from "sonner";

const ALLOWED_VIDEO_EXT = ["mp4", "webm", "mov", "ogv", "ogg"];

/** Extract the video id from any common YouTube link shape. */
function ytId(url) {
  try {
    const u = new URL(url);
    let id = "";
    if (u.hostname.includes("youtu.be")) id = u.pathname.slice(1);
    else if (u.pathname.startsWith("/watch")) id = u.searchParams.get("v") || "";
    else if (u.pathname.startsWith("/live/")) id = u.pathname.split("/live/")[1];
    else if (u.pathname.startsWith("/embed/")) id = u.pathname.split("/embed/")[1];
    else if (u.pathname.startsWith("/shorts/")) id = u.pathname.split("/shorts/")[1];
    id = (id || "").split("/")[0].split("?")[0];
    return id || null;
  } catch (_e) {
    return null;
  }
}

function vimeoEmbed(url) {
  const m = (url || "").match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? `https://player.vimeo.com/video/${m[1]}?autoplay=1&muted=1` : null;
}

function twitchEmbed(url) {
  const m = (url || "").match(/twitch\.tv\/([A-Za-z0-9_]+)/);
  if (!m) return null;
  const parent = typeof window !== "undefined" ? window.location.hostname : "localhost";
  return `https://player.twitch.tv/?channel=${m[1]}&parent=${parent}&autoplay=true&muted=true`;
}

function FeedPlayer({ config }) {
  const [playing, setPlaying] = useState(false);
  if (!config) return null;
  const { mode, provider, url } = config;

  if (mode === "video" && config.file_id) {
    return (
      <video
        data-testid="live-video"
        className="absolute inset-0 w-full h-full object-cover bg-black"
        src={`${API}/live-surf/video?v=${config.file_id}`}
        controls
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
      />
    );
  }

  if (mode === "url" && url) {
    if (provider === "youtube") {
      const id = ytId(url);
      if (id) {
        // Click-to-play: mobile browsers block muted iframe autoplay, so we show
        // a tappable poster (works on every device) that loads the live player.
        if (!playing) {
          return (
            <button
              type="button"
              onClick={() => setPlaying(true)}
              aria-label="Play live surf cam"
              data-testid="live-play"
              className="group absolute inset-0 w-full h-full"
            >
              <img
                src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`}
                alt="Live surf cam"
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <span className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/20 transition-colors">
                <span className="w-16 h-16 rounded-full bg-[color:var(--coral)] text-white flex items-center justify-center shadow-lg group-active:scale-95 transition-transform">
                  <Play className="w-7 h-7 ml-1" fill="currentColor" />
                </span>
              </span>
            </button>
          );
        }
        return (
          <iframe
            data-testid="live-iframe"
            title="Live surf"
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/${id}?autoplay=1&mute=1&playsinline=1&rel=0`}
            frameBorder="0"
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
          />
        );
      }
    }
    if (provider === "vimeo") {
      const e = vimeoEmbed(url);
      if (e) return <iframe data-testid="live-iframe" title="Live surf" className="absolute inset-0 w-full h-full" src={e} frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />;
    }
    if (provider === "twitch") {
      const e = twitchEmbed(url);
      if (e) return <iframe data-testid="live-iframe" title="Live surf" className="absolute inset-0 w-full h-full" src={e} frameBorder="0" allowFullScreen scrolling="no" />;
    }
    if (provider === "file" || provider === "hls") {
      return <video data-testid="live-video" className="absolute inset-0 w-full h-full object-cover bg-black" src={url} controls autoPlay muted loop playsInline />;
    }
    // generic embed fallback
    return <iframe data-testid="live-iframe" title="Live surf" className="absolute inset-0 w-full h-full" src={url} frameBorder="0" allow="autoplay; fullscreen" allowFullScreen />;
  }

  return null;
}

function AdminControls({ config, onChanged }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState(config?.url || "");
  const [caption, setCaption] = useState(config?.caption || "");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const setStream = async () => {
    if (!url.trim()) { toast.error(t("live_admin_url")); return; }
    setBusy(true);
    try {
      await http.post("/admin/live-surf", { mode: "url", url: url.trim(), caption: caption.trim() });
      toast.success(t("live_admin_set_ok"));
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("live_admin_err"));
    } finally { setBusy(false); }
  };

  const clearFeed = async () => {
    setBusy(true);
    try {
      await http.post("/admin/live-surf", { mode: "none" });
      setUrl("");
      toast.success(t("live_admin_set_ok"));
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("live_admin_err"));
    } finally { setBusy(false); }
  };

  const onPickFile = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_VIDEO_EXT.includes(ext)) { toast.error("mp4 / webm / mov / ogg only"); return; }
    if (file.size > 80 * 1024 * 1024) { toast.error("Video too large (80 MB max)"); return; }
    const ct = file.type || (ext === "webm" ? "video/webm" : ext === "mov" ? "video/quicktime" : "video/mp4");
    setBusy(true);
    setProgress(0);
    try {
      const init = await http.post("/admin/live-surf/upload/init", { filename: file.name, content_type: ct, total_size: file.size });
      const uploadId = init.data.upload_id;
      const CHUNK = 2 * 1024 * 1024;
      let offset = 0, index = 0;
      while (offset < file.size) {
        const buf = await file.slice(offset, offset + CHUNK).arrayBuffer();
        await http.post(`/admin/live-surf/upload/chunk?upload_id=${uploadId}&index=${index}`, buf, {
          headers: { "Content-Type": "application/octet-stream" },
        });
        offset += CHUNK; index += 1;
        setProgress(Math.min(99, Math.round((offset / file.size) * 100)));
      }
      await http.post("/admin/live-surf/upload/complete", { upload_id: uploadId, caption: caption.trim() });
      setProgress(100);
      toast.success(t("live_admin_set_ok"));
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("live_admin_err"));
    } finally { setBusy(false); setProgress(0); }
  };

  return (
    <div className="mt-3 border-t border-[color:var(--line)] pt-3" data-testid="live-admin">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 text-xs font-semibold text-[color:var(--ink-2)] hover:text-[color:var(--coral)]"
        data-testid="live-admin-toggle"
      >
        <Settings2 className="w-3.5 h-3.5" /> {t("live_admin_title")}
      </button>

      {open && (
        <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_auto]">
          <input
            data-testid="live-admin-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={t("live_admin_url_ph")}
            className="w-full rounded-xl border border-[color:var(--line)] px-3 py-2 text-sm bg-white"
          />
          <button
            type="button"
            onClick={setStream}
            disabled={busy}
            data-testid="live-admin-set"
            className="rounded-xl bg-[color:var(--navy)] text-white text-sm font-semibold px-4 py-2 disabled:opacity-60 whitespace-nowrap"
          >
            {t("live_admin_set")}
          </button>

          <input
            data-testid="live-admin-caption"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder={t("live_admin_caption")}
            className="w-full rounded-xl border border-[color:var(--line)] px-3 py-2 text-sm bg-white sm:col-span-2"
          />

          <div className="flex items-center gap-2 sm:col-span-2 flex-wrap">
            <input ref={fileRef} type="file" accept="video/*" onChange={onPickFile} className="hidden" data-testid="live-admin-file" />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              data-testid="live-admin-upload"
              className="inline-flex items-center gap-2 rounded-xl bg-[color:var(--coral)] text-white text-sm font-semibold px-4 py-2 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {busy && progress > 0 ? `${t("live_admin_uploading")} ${progress}%` : t("live_admin_upload")}
            </button>
            <button
              type="button"
              onClick={clearFeed}
              disabled={busy}
              data-testid="live-admin-clear"
              className="inline-flex items-center gap-2 rounded-xl border border-[color:var(--line)] text-sm font-semibold px-4 py-2 disabled:opacity-60 text-[color:var(--ink-2)]"
            >
              <X className="w-4 h-4" /> {t("live_admin_clear")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function LiveSurfReport() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await http.get("/live-surf");
      setConfig(res.data);
    } catch (_e) {
      setConfig({ mode: "none" });
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isLive = config && config.mode && config.mode !== "none";
  const isAdmin = user?.role === "admin";

  // Regular visitors only ever see the card once a real live feed is confirmed
  // (no empty-state flash while loading). Admins always see it for the controls.
  if (!isAdmin && (!loaded || !isLive)) return null;

  return (
    <div className="clay p-4 sm:p-5" data-testid="live-surf-report">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-2xl bg-[color:var(--coral)]/10 text-[color:var(--coral)] flex items-center justify-center">
            <Waves className="w-5 h-5" />
          </span>
          <div>
            <h2 className="text-lg sm:text-xl font-bold leading-tight" style={{ fontFamily: "Outfit" }}>
              {t("live_title")}
            </h2>
            {config?.updated_at && (
              <p className="text-[11px] text-[color:var(--ink-2)]">
                {t("live_updated")} {new Date(config.updated_at).toLocaleString()}
              </p>
            )}
          </div>
        </div>
        {isLive && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--emergency)]/10 text-[color:var(--emergency)] px-3 py-1 text-xs font-bold" data-testid="live-badge">
            <Radio className="w-3.5 h-3.5 animate-pulse" /> {t("live_badge")}
          </span>
        )}
      </div>

      {isLive ? (
        <>
          <div className="relative w-full overflow-hidden rounded-2xl bg-black aspect-video">
            <FeedPlayer config={config} />
          </div>
          {config.caption && (
            <p className="mt-2 text-sm text-[color:var(--ink-2)]" data-testid="live-caption">{config.caption}</p>
          )}
        </>
      ) : (
        <div className="relative w-full overflow-hidden rounded-2xl aspect-video flex flex-col items-center justify-center text-center bg-gradient-to-br from-[color:var(--navy)] to-[color:var(--teal,#1B6B93)]" data-testid="live-empty">
          <Video className="w-10 h-10 text-white/80 mb-2" />
          <p className="text-white font-semibold px-4" style={{ fontFamily: "Outfit" }}>{t("live_none_title")}</p>
          <p className="text-white/70 text-sm px-6 mt-1 max-w-md">{t("live_none_sub")}</p>
        </div>
      )}

      {isAdmin && <AdminControls config={config} onChanged={load} />}
    </div>
  );
}
